import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import WrapHeadingText from "./WrapHeadingText";
import { debounce } from "../utility/UtilityFunctions";

gsap.registerPlugin(ScrollTrigger);

export default class AnimatedHero {
    constructor(element) {
        this.element = {
            base: element,
            slider: element.querySelector('.c-animated-hero__media-container'),
            preheading: element.querySelector('.c-animated-hero__preheading'),
            heading: element.querySelector('.c-animated-hero__heading'),
            subheading: element.querySelector('.c-animated-hero__subheading'),
            logo: element.querySelector('.c-animated-hero__logo'),
            buttonGroup: element.querySelector('.c-animated-hero__button-group'),
            buttonBack: element.querySelector('.c-animated-hero__inner > .c-button--back'),
            buttonShare: element.querySelector('.c-animated-hero__inner > .c-button--share'),
        };

        this.isDesktop = window.innerWidth >= 1024;
        this.timeline = null;
        this.animId = 'hero-animation';

        this.init();
        this.handleResize();
    }

    init() {
        if (this.element.heading) {
            new WrapHeadingText(this.element.base, '.c-animated-hero__heading');
            this.element.headingFirstSpan = this.element.heading.querySelector('span:first-of-type');
            this.element.headingSecondSpan = this.element.heading.querySelector('span:last-of-type');
        }

        this.setupInitialScale();
        this.createScrollAnimation();
    }

    calculateInsetClipPath(el, targetWidth, targetHeight, topOffset = 0, bottomOffset = 0) {
        const actualWidth = el.offsetWidth;
        const actualHeight = el.offsetHeight;

        const scaleX = targetWidth / actualWidth;
        const scaleY = targetHeight / actualHeight;

        const visibleWidth = actualWidth * scaleX;
        const visibleHeight = actualHeight * scaleY;

        const horizontalInset = ((actualWidth - visibleWidth) / 2) / actualWidth * 100;
        const verticalInset = ((actualHeight - visibleHeight) / 2) / actualHeight * 100;

        const round = n => Math.round(n * 1000) / 1000;

        const top = round(verticalInset + topOffset);
        const right = round(horizontalInset);
        const bottom = round(verticalInset + bottomOffset);
        const left = round(horizontalInset);

        return `inset(${top}% ${right}% ${bottom}% ${left + .001}%)`;
    }

    setupInitialScale() {
        const { slider } = this.element;

        const isDesktop = window.innerWidth >= 1024;
        const aspectWidth = isDesktop ? 685 : 280; // 310
        const aspectHeight = isDesktop ? 350 : 160; // 340

        // Adjust offsets per platform
        const topOffset = isDesktop ? 0 : -4;     // move view up slightly on mobile
        const bottomOffset = isDesktop ? 2 : 4;   // extend bottom on both, more on mobile

        const startClip = this.calculateInsetClipPath(
            slider,
            aspectWidth,
            aspectHeight,
            topOffset,
            bottomOffset
        );

        // Set initial state
        gsap.set(slider, { clipPath: startClip });
    }

    createScrollAnimation() {
        const { base, slider, preheading, headingFirstSpan, headingSecondSpan, logo, subheading, text, buttonGroup, buttonBack, buttonShare } = this.element;

        if (this.timeline) {
            this.timeline.kill();
            this.timeline = null;
        }

        this.timeline = gsap.timeline({
            scrollTrigger: {
                trigger: base,
                id: this.animId,
                start: "top top",
                end: "+=160%",
                scrub: 1,
                pin: true,
            },
            onUpdate: () => {
                base.classList.toggle('content-hidden', this.timeline.progress() >= 0.65);
                base.classList.toggle('animation-complete', this.timeline.progress() >= 0.75);
            }
        });

        // Media container scale
        this.timeline.to(slider, {
            clipPath: "inset(0% 0% 0% 0%)",
            ease: "power2.inOut",
            duration: 2
        });

        // Move + fade logo only (as it moves)
        this.translate(this.timeline, [logo].filter(Boolean), "up");

        // Fade and optionally scale text/button elements (no movement)
        const fadingElements = [preheading, headingFirstSpan, headingSecondSpan, subheading, text, buttonGroup, buttonBack, buttonShare].filter(Boolean);

        this.timeline.to(fadingElements, {
            opacity: 0,
            scale: this.isDesktop ? 0.8 : 0.9,
            duration: 0.8,
            ease: "power1.out"
        }, "-=1.5");

        // Slight pause before releasing scroll
        this.timeline.to({}, { duration: 0.5 });
    }

    translate(tl, elements, direction) {
        if (!elements.length > 0) return

        tl.to(elements, {
            y: direction === "up" ? -30 : 30,
            // scale: el => el.classList.contains('big') ? 1.3 : 1.1, // next project apply helper classes to allow for dyncamic scaling by element
            scale: this.isDesktop ? 1.35 : 1.15,
            opacity: 0,
            duration: 0.8,
            ease: "power1.out"
        }, "-=1.5");
    }

    handleResize() {
        window.addEventListener("resize", debounce(() => {
            this.isDesktop = window.innerWidth >= 1024;

            this.refresh();
            this.setupInitialScale();
            this.createScrollAnimation(); // Recalculate everything
        }, 250));
    }

    refresh() {
        ScrollTrigger.getById(this.animId)?.kill();
        ScrollTrigger.refresh();
    }
}